import React, { useContext, useRef, useMemo, useState, useEffect } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
import { isSSR } from '@front10/utils';
/**
 * Atoms
 */
import OriginDestinationFilter from '../InlineFilters/OriginDestinationFilter';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';
import nanoid from 'nanoid';

const RouteFilter = (props, ref) => {
  const [id] = React.useState(nanoid());
  const {
    disabled,
    originDestinationSettings,
    locations: {
      origin: { data: originData, value: originValue },
      destination: { data: destinationData, value: destinationValue }
    },
    locations,
    showLabelfilterButton,
    showDescriptionLabel,
    showSeparatorDescriptionLabel = false,
    fromLabelKey = 'cmp-route-filter-button-from-label',
    toLabelKey = 'cmp-route-filter-button-to-label',
    doneButtonLabelKey = 'cmp-route-done-button',
    clearButtonLabelKey = 'cmp-route-clear-label',
    popover = {},
    alwaysEnabledDoneButton = true
  } = props;

  const [saveorigen, setSaveOrigen] = useState(null);
  const [saveDestinationLabel, setSaveDestinationLabel] = useState('');
  const [savedestination, setSaveDestination] = useState(null);
  const [activeDone, setActiveDone] = useState(false);
  const oref = useRef();
  const dref = useRef();
  const flexibleRef = useRef();

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const [forcedLoading, setForcedLoading] = useState(false);
  const [isClearOriginFocus, setIsClearOriginFocus] = useState(false);
  const [isClearDestinationFocus, setIsClearDestinationFocus] = useState(false);
  const enableAriaHiddenFooter = useRef(false);
  const setEnableAriaHiddenFooter = (value) => {
    enableAriaHiddenFooter.current = value;
  };

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        if (!isSSR()) {
          setTimeout(() => {
            document.querySelector(`#toggleElementId-${id}`).focus();
          }, 200);
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * Doing this transformation in the config for when
   * you remove the value from the origin filter the
   * component will focus on the correct input
   */
  const originRef = useRef();
  const newOriginDestinationSettings = {
    ...originDestinationSettings,
    components: {
      ...originDestinationSettings.components,
      origin: {
        inputFocusReferences: originRef,
        ref: oref,
        enableClearInputButton: true,
        ...originDestinationSettings.components.origin
      },
      destination: {
        ref: dref,
        enableClearInputButton: true,
        ...originDestinationSettings.components.destination
      }
    }
  };

  const validateDestination = (value) => {
    const tmp = destinationData.filter((i) => i.value === value).pop()?.label;
    if (!tmp) {
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    const handleLocationsChange = () => {
      if (locations.origin.valueSelect && destinationData.length > 0) {
        const p1 = validateDestination(locations.destination.valueSelect);
        const p2 = new Promise((resolve) => {
          setForcedLoading(true);
          setTimeout(() => {
            resolve(true);
          }, 250);
        });

        Promise.all([p1, p2]).then(([resultP1, resultP2]) => {
          if (!resultP1) {
            locations.destination.selectDestination({ value: '' });
          }
          setForcedLoading(false);
        });
      }

      if (
        (Boolean(locations.origin.value) || Boolean(locations.destination.value)) &&
        locations.origin.valueSelect === '' &&
        locations.destination.valueSelect === ''
      ) {
        setActiveDone(true);
      } else {
        setActiveDone(false);
      }
    };

    handleLocationsChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locations.origin.value,
    locations.destination.value,
    locations.origin.valueSelect,
    locations.destination.valueSelect
  ]);

  const any = translate('cmp-route-anywhere', 'Anywhere');

  const originLabel = useMemo(() => {
    if (!originValue) {
      return any;
    }
    const label = originData.filter((i) => i.value === originValue).pop()?.label;
    if (label) {
      if (label.includes('empty')) {
        return translate('cmp-origin-destination-empty', 'Airport Name is Empty');
      } else if (label.includes('not-available')) {
        return translate(`cmp-origin-destination-not-available`, 'Airport is not available');
      }
      return label;
    }
    return '';
  }, [originValue, originData, any, translate]);

  const destinationLabel = useMemo(() => {
    if (!destinationValue) {
      return any;
    }
    let search = '';
    search = destinationData.filter((i) => i.value === destinationValue).pop()?.label;
    if (search?.includes('empty')) {
      return translate('cmp-origin-destination-empty', 'Airport Name is Empty');
    } else if (search?.includes('not-available')) {
      return translate(`cmp-origin-destination-not-available`, 'Airport is not available');
    }

    return search;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationData, destinationValue, translate]);

  const label = translate('cmp-route-title', 'Select route');
  const labelSeparator = translate('cmp-route-title-separador', ':');

  const buttonLabel = useMemo(() => {
    const parts = [];

    if ((originValue || destinationValue) && !disabled) {
      if (showDescriptionLabel) {
        parts.push(label);
      }
      if (showLabelfilterButton) {
        parts.push(translate(fromLabelKey, 'From'));
      }
      parts.push(originLabel);
      parts.push('-');
      if (showLabelfilterButton) {
        parts.push(translate(toLabelKey, 'To'));
      }
      parts.push(destinationLabel !== undefined ? destinationLabel : saveDestinationLabel);
    } else if (showDescriptionLabel) {
      parts.push(label);
      if (showSeparatorDescriptionLabel) {
        parts.push(labelSeparator);
      }
      parts.push(any);
    } else {
      parts.push(label);
    }
    if (direction === 'rtl') {
      return parts.reverse().join('\u200E ');
    }
    return parts.join(' ');
  }, [
    originValue,
    destinationValue,
    disabled,
    showDescriptionLabel,
    showLabelfilterButton,
    label,
    labelSeparator,
    any,
    originLabel,
    destinationLabel,
    saveDestinationLabel,
    direction,
    fromLabelKey,
    showSeparatorDescriptionLabel,
    toLabelKey,
    translate
  ]);
  var timer;

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>
        <FlexibleFilterContainer
          ref={flexibleRef}
          mobileView={originDestinationSettings.isMobile ?? false}
          showSeparator
          saveOnClose={false}
          variant="layout.popover"
          label={label}
          title={label}
          disabledDoneButton={
            alwaysEnabledDoneButton === true
              ? false
              : activeDone
              ? !activeDone
              : !Boolean(locations.origin.valueSelect) &&
                !Boolean(locations.destination.valueSelect)
          }
          disabledClearButton={
            !Boolean(locations.origin.valueSelect) && !Boolean(locations.destination.valueSelect)
          }
          labelBottomBtn={translate(doneButtonLabelKey, 'Done')}
          labelClearBtn={translate(clearButtonLabelKey, 'Clear')}
          enabledClearButton
          enabledDoneButton
          ariaHiddenFooterButtons={enableAriaHiddenFooter.current}
          onOpen={() => {
            if (!originValue && originValue !== locations.origin.valueSelect) {
              locations.origin.selectOrigin({ value: '' });
            }
            if (!destinationValue && destinationValue !== locations.destination.valueSelect) {
              locations.destination.selectDestination({ value: '' });
            }
            if (activeDone) {
              setActiveDone(false);
            }
            if (originDestinationSettings.isMobile) {
              setTimeout(() => {
                if (document) {
                  const tmp = document?.querySelector(
                    '[data-testid="locationSelector-destination"]'
                  );
                  if (tmp) {
                    const aux = tmp?.querySelector('input').getAttribute('value');
                    if (aux) {
                      setSaveDestinationLabel(aux);
                    }
                  }
                }
              }, 200);
            }
          }}
          onClearBtn={() => {
            originRef.current.focus();
            locations.origin.selectOrigin({ value: '' });
            locations.destination.selectDestination({ value: '' });
            setSaveOrigen(null);
            setSaveDestination(null);
            if (originValue) {
              locations.origin.onClear();
            }
            if (destinationValue) {
              locations.destination.onClear();
            }
            oref.current.onClear();
            dref.current.onClear();
            setActiveDone(true);
          }}
          onDismiss={() => {
            if (originValue && originValue !== locations.origin.valueSelect) {
              locations.origin.selectOrigin({ value: originValue });
              oref.current?.update(originValue);
            }

            if (destinationValue && destinationValue !== locations.destination.valueSelect) {
              locations.destination.selectDestination({ value: destinationValue });
              oref.current?.update(destinationValue);
            } else if (!locations.destination.valueSelect) {
              locations.destination.selectDestination({ value: locations.destination.value });
              setSaveDestination(locations.destination.value);
            }
            if (activeDone) {
              setActiveDone(false);
            }
          }}
          onOpenPopup={() => {
            if (!originDestinationSettings.isMobile) {
              if (document) {
                const tmp = document?.querySelector('[data-testid="locationSelector-destination"]');
                if (tmp) {
                  const aux = tmp?.querySelector('input').getAttribute('value');
                  if (aux) {
                    setSaveDestinationLabel(aux);
                  }
                }
              }
            }
            if (!locations.origin.valueSelect && locations.origin.value) {
              locations.origin.selectOrigin({ value: locations.origin.value });
            } else if (
              originValue &&
              saveorigen !== locations.origin.valueSelect &&
              saveorigen === originValue
            ) {
              locations.origin.selectOrigin({ value: saveorigen });
              oref.current.update(saveorigen);
            } else if (!originValue && originValue !== locations.origin.valueSelect) {
              locations.origin.selectOrigin({ value: '' });
              oref.current?.update('');
            }

            if (!locations.destination.valueSelect && locations.destination.value) {
              locations.destination.selectDestination({ value: locations.destination.value });
            } else if (
              destinationValue &&
              savedestination !== locations.destination.valueSelect &&
              savedestination === destinationValue
            ) {
              dref.current.update(savedestination);
              locations.destination.selectDestination({ value: savedestination });
            } else if (
              !destinationValue &&
              destinationValue !== locations.destination.valueSelect
            ) {
              locations.destination.selectDestination({ value: '' });
              dref.current?.update('');
            }
            if (activeDone) {
              setActiveDone(false);
            }
            setTimeout(() => originRef.current.focus(), 100);
          }}
          toggleElement={
            <FilterButton
              id={`toggleElementId-${id}`}
              iconSettings={originDestinationSettings?.components?.filterButtonIconProps}
              value={buttonLabel}
              aria-label={buttonLabel}
              active={Boolean(originValue) || Boolean(destinationValue)}
              disabled={disabled} // used to disabled the component
              onFocus={() => {
                if (flexibleRef.current.isOpen) {
                  timer = setTimeout(() => {
                    flexibleRef?.current?.close();
                  }, 150);
                }
              }}
              onBlur={() => {
                clearTimeout(timer);
              }}
            />
          }
          onSave={() => {
            if (
              Boolean(locations.origin.valueSelect) &&
              locations.origin.value !== locations.origin.valueSelect
            ) {
              locations.origin.onChange({
                value: locations.origin.valueSelect
              });
              setSaveOrigen(locations.origin.valueSelect);
            } else if (Boolean(locations.origin.value) && locations.origin.valueSelect === '') {
              locations.origin.onChange({
                value: undefined
              });
              setSaveOrigen('');
            }

            if (
              Boolean(locations.destination.valueSelect) &&
              locations.destination.value !== locations.destination.valueSelect
            ) {
              locations.destination.onChange({
                value: locations.destination.valueSelect
              });
              setSaveDestination(locations.destination.valueSelect);
            } else if (
              Boolean(locations.destination.value) &&
              locations.destination.valueSelect === ''
            ) {
              locations.destination.onChange({
                value: undefined
              });
              setSaveDestination('');
            }
          }}
          useAsDialog
          focusLockInside
          {...popover}
          dialogAriaLabel={translate(
            popover.dialogAriaLabel ?? 'cmp-route-filter-dialog-aria-label',
            'Route filter'
          )}
        >
          <Panels showSeparator={false}>
            <OriginDestinationFilter
              {...newOriginDestinationSettings}
              {...locations}
              setIsClearOriginFocus={setIsClearOriginFocus}
              setIsClearDestinationFocus={setIsClearDestinationFocus}
              isClearOriginFocus={isClearOriginFocus}
              isClearDestinationFocus={isClearDestinationFocus}
              setEnableAriaHiddenFooter={setEnableAriaHiddenFooter}
              secondary={true}
              forcedLoading={forcedLoading}
            />
          </Panels>
        </FlexibleFilterContainer>
      </ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(RouteFilter);
